import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Lock, ShieldCheck, Clock3, UserCheck,
} from "lucide-react";

import { getUtm } from "../utils/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = {
  auto: {
    label: "Assurance Auto",
    insuranceType: "Assurance Automobile",
    steps: [
      {
        title: "Votre véhicule",
        fields: [
          { name: "Marque", type: "text", placeholder: "ex. Peugeot", required: true },
          { name: "Modèle", type: "text", placeholder: "ex. 3008", required: true },
          { name: "Année de mise en circulation", type: "chips", options: ["2024 — 2026", "2020 — 2023", "2015 — 2019", "Avant 2015"], required: true },
          { name: "Énergie", type: "chips", options: ["Essence", "Diesel", "Hybride", "Électrique"], required: true },
        ],
      },
      {
        title: "Votre profil conducteur",
        fields: [
          { name: "Date d'obtention du permis", type: "month", required: true },
          { name: "Bonus-malus actuel", type: "chips", options: ["0,50 (bonus maximum)", "0,51 à 0,90", "0,91 à 1,00", "Malus (sup. à 1,00)", "Je ne sais pas"], required: true },
          { name: "Usage du véhicule", type: "chips", options: ["Privé", "Trajets domicile-travail", "Professionnel"], required: true },
        ],
      },
    ],
  },
  habitation: {
    label: "Assurance Habitation",
    insuranceType: "Assurance Habitation",
    steps: [
      {
        title: "Votre logement",
        fields: [
          { name: "Vous êtes", type: "chips", options: ["Locataire", "Propriétaire occupant", "Propriétaire bailleur"], required: true },
          { name: "Type de logement", type: "chips", options: ["Appartement", "Maison"], required: true },
          { name: "Surface habitable", type: "chips", options: ["Moins de 40 m²", "40 à 80 m²", "80 à 120 m²", "Plus de 120 m²"], required: true },
          { name: "Usage", type: "chips", options: ["Résidence principale", "Résidence secondaire", "Investissement locatif"], required: true },
        ],
      },
    ],
  },
  sante: {
    label: "Mutuelle Santé",
    insuranceType: "Complémentaire Santé",
    steps: [
      {
        title: "Votre situation",
        fields: [
          { name: "Votre régime", type: "chips", options: ["Salarié", "Indépendant / TNS", "Retraité", "Frontalier", "Sans activité"], required: true },
          { name: "À assurer", type: "chips", options: ["Vous seul", "Vous + conjoint", "Famille (avec enfants)"], required: true },
          { name: "Votre âge", type: "number", placeholder: "ex. 42", required: true },
        ],
      },
      {
        title: "Vos priorités de remboursement",
        fields: [
          { name: "Postes importants pour vous", type: "multichips", options: ["Dentaire", "Optique", "Hospitalisation", "Médecines douces", "Audiologie", "Aucune en particulier"], required: true },
        ],
      },
    ],
  },
  prevoyance: {
    label: "Prévoyance",
    insuranceType: "Prévoyance",
    steps: [
      {
        title: "Votre situation",
        fields: [
          { name: "Votre âge", type: "number", placeholder: "ex. 45", required: true },
          { name: "Situation professionnelle", type: "chips", options: ["Salarié", "Indépendant / TNS", "Dirigeant", "Sans activité"], required: true },
          { name: "Votre objectif principal", type: "chips", options: ["Protéger ma famille", "Maintenir mes revenus", "Préparer la dépendance"], required: true },
          { name: "Capital souhaité", type: "chips", options: ["Moins de 100 000 €", "100 000 à 250 000 €", "Plus de 250 000 €", "À définir ensemble"], required: true },
        ],
      },
    ],
  },
  vie: {
    label: "Assurance Vie",
    insuranceType: "Assurance Vie & Épargne",
    steps: [
      {
        title: "Votre projet d'épargne",
        fields: [
          { name: "Votre objectif", type: "chips", options: ["Préparer ma retraite", "Transmettre à mes proches", "Financer un projet", "Épargne de précaution"], required: true },
          { name: "Versement initial envisagé", type: "chips", options: ["Moins de 1 000 €", "1 000 à 10 000 €", "10 000 à 50 000 €", "Plus de 50 000 €"], required: true },
          { name: "Versements réguliers", type: "chips", options: ["Oui, chaque mois", "Non, ponctuellement", "Je ne sais pas encore"], required: true },
          { name: "Votre profil d'investisseur", type: "chips", options: ["Sécurité avant tout", "Équilibré", "Dynamique"], required: true },
        ],
      },
    ],
  },
  professionnels: {
    label: "Assurance Professionnels",
    insuranceType: "Assurance Professionnels / Entreprise",
    steps: [
      {
        title: "Votre activité",
        fields: [
          { name: "Profession / activité", type: "text", placeholder: "ex. Boulangerie, consultant IT…", required: true },
          { name: "Forme juridique", type: "chips", options: ["Auto-entreprise", "EURL / SASU", "SARL / SAS", "Autre / en création"], required: true },
          { name: "Effectif", type: "chips", options: ["Vous seul", "1 à 5 salariés", "6 à 20 salariés", "Plus de 20"], required: true },
          { name: "Garanties recherchées", type: "multichips", options: ["RC Pro", "Décennale", "Multirisque locaux / matériel", "Prévoyance dirigeant"], required: true },
        ],
      },
    ],
  },
  "rc-pro": {
    label: "RC Professionnelle",
    insuranceType: "Assurance Professionnels / Entreprise",
    produit: "RC Pro",
    steps: [
      {
        title: "Votre activité",
        fields: [
          { name: "Profession exercée", type: "text", placeholder: "ex. Architecte, coiffeur, consultant…", required: true },
          { name: "Chiffre d'affaires annuel", type: "chips", options: ["Moins de 50 000 €", "50 000 à 150 000 €", "150 000 à 500 000 €", "Plus de 500 000 €"], required: true },
          { name: "Effectif", type: "chips", options: ["Vous seul", "1 à 5 salariés", "Plus de 5"], required: true },
          { name: "Actuellement assuré en RC Pro ?", type: "chips", options: ["Oui", "Non", "Résilié récemment"], required: true },
        ],
      },
    ],
  },
  decennale: {
    label: "Assurance Décennale",
    insuranceType: "Assurance Professionnels / Entreprise",
    produit: "Décennale",
    steps: [
      {
        title: "Votre entreprise du bâtiment",
        fields: [
          { name: "Corps d'état / activités", type: "text", placeholder: "ex. Maçonnerie, plomberie, électricité…", required: true },
          { name: "Ancienneté de l'entreprise", type: "chips", options: ["En création", "Moins de 2 ans", "2 à 5 ans", "Plus de 5 ans"], required: true },
          { name: "Chiffre d'affaires annuel", type: "chips", options: ["Moins de 100 000 €", "100 000 à 300 000 €", "Plus de 300 000 €"], required: true },
          { name: "Historique d'assurance", type: "chips", options: ["Jamais assuré", "Actuellement assuré", "Résilié / sinistres récents"], required: true },
        ],
      },
    ],
  },
  frontalier: {
    label: "Frontalier Monaco",
    insuranceType: null,
    insuranceTypeFrom: "Votre besoin principal",
    produit: "Frontalier Monaco",
    steps: [
      {
        title: "Votre situation de frontalier",
        fields: [
          { name: "Votre régime de base actuel", type: "chips", options: ["Caisses Sociales de Monaco (CCSS)", "Régime français (CPAM)", "Couverture privée", "Je ne sais pas"], required: true },
          { name: "Votre employeur", type: "chips", options: ["Secteur privé à Monaco", "Fonction publique monégasque", "Indépendant à Monaco", "Autre situation"], required: true },
          { name: "Qui souhaitez-vous assurer ?", type: "chips", options: ["Moi uniquement", "Moi + conjoint", "Toute la famille"], required: true },
        ],
      },
      {
        title: "Votre besoin",
        fields: [
          { name: "Votre besoin principal", type: "chips", options: ["Mutuelle santé", "Prévoyance", "Les deux (santé + prévoyance)", "Je ne sais pas encore"], required: true },
          { name: "Vos priorités", type: "multichips", options: ["Dentaire", "Optique", "Hospitalisation", "Maintien de revenu", "Capital décès", "À définir avec le conseiller"], required: true },
        ],
      },
    ],
  },
  general: {
    label: "Étude d'assurance gratuite",
    insuranceType: null,
    steps: [
      {
        title: "Votre besoin",
        fields: [
          { name: "Type d'assurance", type: "chips", options: ["Assurance Automobile", "Assurance Habitation", "Complémentaire Santé", "Prévoyance", "Assurance Vie & Épargne", "Assurance Professionnels / Entreprise", "Pack Multirisque Global"], required: true },
        ],
      },
    ],
  },
};

const chipCls = (active) =>
  `rounded-2xl border px-5 py-3.5 text-sm font-medium transition-all duration-300 text-left ${
    active
      ? "border-amber/60 bg-amber/10 text-amber shadow-[0_0_25px_rgba(212,175,55,0.15)]"
      : "border-ivory/10 bg-white/[0.03] text-ivory/60 hover:border-ivory/30 hover:text-ivory"
  }`;

const inputCls =
  "w-full rounded-xl border border-ivory/10 bg-white/5 px-5 py-4 text-base text-ivory placeholder:text-ivory/30 transition-all duration-300 gold-focus";

const slug = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Carte réassurance : le visage du conseiller derrière la promesse de rappel
const AdvisorCard = ({ centered = false, testid }) => (
  <div data-testid={testid} className={`flex items-center gap-4 ${centered ? "justify-center text-left" : ""}`}>
    <img
      src="/images/yohan-courtois.jpg"
      alt="Yohan Courtois, votre conseiller dédié ASSU-FRANCE"
      className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-amber/40"
      loading="lazy"
    />
    <div>
      <p className="text-sm font-semibold text-ivory">Yohan Courtois, fondateur d'ASSU-FRANCE</p>
      <p className="text-xs leading-relaxed text-ivory/55">
        C'est moi ou l'un de mes collaborateurs qui vous rappelle, jamais un robot ou une IA.
      </p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/35">
        ORIAS n° 25003318
      </p>
    </div>
  </div>
);


export default function Quote() {
  const { product } = useParams();
  const config = PRODUCTS[product] || PRODUCTS.general;

  const contactFields = [
    { name: "Nom & Prénom", type: "text", placeholder: "ex. Jean Dupont", required: true },
    { name: "Adresse email", type: "email", placeholder: "jean.dupont@exemple.fr", required: true },
    { name: "Téléphone mobile", type: "tel", placeholder: "06 12 34 56 78", required: true },
    { name: "Code postal", type: "text", placeholder: "ex. 06500", required: true },
    { name: "Ville", type: "text", placeholder: "ex. Menton", required: true },
    { name: "Créneau de rappel souhaité", type: "chips", options: ["Dès que possible", "Le matin (9h – 12h)", "L'après-midi (14h – 18h)", "En soirée (18h – 20h)"], required: false },
    { name: "Précisions (optionnel)", type: "textarea", placeholder: "Tout élément utile pour votre conseiller…", required: false },
  ];

  const steps = useMemo(() => [...config.steps, { title: "Vos coordonnées", fields: contactFields }], [config]);
  const totalSteps = steps.length;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [touched, setTouched] = useState(false);

  const current = steps[step];
  const isLast = step === totalSteps - 1;

  const setAnswer = (field, value) => {
    if (field.type === "multichips") {
      const prev = answers[field.name] || [];
      setAnswers({
        ...answers,
        [field.name]: prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
      });
    } else {
      setAnswers({ ...answers, [field.name]: value });
    }
  };

  const stepValid = current.fields.every((f) => {
    if (!f.required) return true;
    const v = answers[f.name];
    if (f.type === "multichips") return Array.isArray(v) && v.length > 0;
    return v !== undefined && String(v).trim() !== "";
  });

  const goNext = () => {
    if (!stepValid) {
      setTouched(true);
      return;
    }
    setTouched(false);
    if (isLast) {
      submit();
    } else {
      setStep(step + 1);
    }
  };

  const goBack = () => {
    setTouched(false);
    setStep(Math.max(0, step - 1));
  };

  const submit = async () => {
    setLoading(true);
    const details = { ...answers };
    delete details["Nom & Prénom"];
    delete details["Adresse email"];
    delete details["Téléphone mobile"];
    delete details["Code postal"];
    delete details["Ville"];
    delete details["Précisions (optionnel)"];
    if (config.produit) details["Produit"] = config.produit;
    const insuranceType =
      config.insuranceType || answers["Type d'assurance"] || "Pack Multirisque Global";
    if (!config.insuranceType) delete details["Type d'assurance"];
    let finalType = insuranceType;
    if (config.insuranceTypeFrom) {
      const v = String(answers[config.insuranceTypeFrom] || "");
      finalType = /prévoyance/i.test(v) && !/santé|deux|mutuelle/i.test(v) ? "Prévoyance" : "Complémentaire Santé";
    }
    try {
      await axios.post(`${API}/leads`, {
        full_name: answers["Nom & Prénom"],
        email: answers["Adresse email"],
        phone: answers["Téléphone mobile"],
        postal_code: answers["Code postal"],
        city: answers["Ville"],
        insurance_type: finalType,
        message: answers["Précisions (optionnel)"] || "",
        product,
        details,
        ...getUtm(),
      });
      setDone(true);
      if (window.fbq) window.fbq("track", "Lead", { content_name: config.title });
      toast.success("Votre demande de devis a été envoyée");
    } catch (err) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (f) => {
    const value = answers[f.name];
    const missing = touched && f.required && (f.type === "multichips" ? !(value && value.length) : !value);
    return (
      <div key={f.name} className="space-y-3">
        <label className="block text-sm font-medium text-ivory/80">
          {f.label || f.name} {f.required ? <span className="text-amber">*</span> : <span className="text-ivory/30">(optionnel)</span>}
        </label>
        {f.type === "chips" || f.type === "multichips" ? (
          <div className="flex flex-wrap gap-3" data-testid={`quote-field-${slug(f.name)}`}>
            {f.options.map((opt) => {
              const active = f.type === "multichips" ? (value || []).includes(opt) : value === opt;
              return (
                <button key={opt} type="button" onClick={() => setAnswer(f, opt)} className={chipCls(active)}>
                  {opt}
                </button>
              );
            })}
          </div>
        ) : f.type === "textarea" ? (
          <textarea
            rows={3}
            value={value || ""}
            onChange={(e) => setAnswer(f, e.target.value)}
            placeholder={f.placeholder}
            data-testid={`quote-field-${slug(f.name)}`}
            className={`${inputCls} resize-none`}
          />
        ) : (
          <input
            type={f.type}
            value={value || ""}
            onChange={(e) => setAnswer(f, e.target.value)}
            placeholder={f.placeholder}
            data-testid={`quote-field-${slug(f.name)}`}
            className={inputCls}
          />
        )}
        {missing && <p className="text-xs text-red-300">Ce champ est nécessaire pour continuer.</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory" data-testid="quote-page">
      <div className="grain-overlay" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 45% at 50% 0%, rgba(212,175,55,0.08), transparent 60%)" }}
      />
      <header className="relative border-b hairline">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="quote-brand" className="font-serif text-2xl font-semibold tracking-tight">
            ASSU<span className="text-amber inline-block" style={{ transform: "translateY(-0.1em)" }}>–</span>FRANCE
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40" data-testid="quote-product-label">
            {config.label}
          </span>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-8 py-14 sm:py-20">
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl border hairline bg-cardbg p-10 sm:p-14 text-center"
            data-testid="quote-success"
          >
            <CheckCircle2 className="mx-auto h-16 w-16 text-amber" />
            <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold">Votre demande est envoyée&nbsp;!</h1>
            <p className="mt-4 text-base leading-relaxed text-ivory/60 max-w-md mx-auto">
              Un conseiller ASSU-FRANCE étudie votre dossier et vous recontacte
              <strong className="text-ivory"> sous 24 heures ouvrées</strong> avec un devis personnalisé.
            </p>
            <div className="mt-8 inline-block rounded-2xl border hairline bg-white/[0.02] p-4 sm:p-5">
              <AdvisorCard centered testid="quote-advisor-success" />
            </div>
            <Link
              to="/"
              data-testid="quote-back-home"
              className="mt-8 inline-block font-mono text-xs uppercase tracking-[0.2em] text-amber border-b border-amber/40 pb-1 hover:text-amber-warm transition-colors"
            >
              Retour à l'accueil
            </Link>
          </motion.div>
        ) : (
          <>
            <div className="mb-10" data-testid="quote-progress">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40">
                  Étape {step + 1} sur {totalSteps}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">
                  {Math.round(((step + 1) / totalSteps) * 100)}&nbsp;%
                </p>
              </div>
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-amber rounded-full"
                  animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl border hairline bg-cardbg p-8 sm:p-12"
                data-testid={`quote-step-${step + 1}`}
              >
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mb-2">
                  {current.title}
                </h1>
                <p className="text-sm text-ivory/50 mb-9">
                  {isLast
                    ? "Dernière étape : votre conseiller dédié vous rappelle sous 24h ouvrées."
                    : "Quelques précisions suffisent pour un devis vraiment adapté."}
                </p>
                {isLast && (
                  <div className="mb-9 rounded-2xl border hairline bg-white/[0.02] p-4 sm:p-5">
                    <AdvisorCard testid="quote-advisor-card" />
                  </div>
                )}
                <div className="space-y-7">{current.fields.map(renderField)}</div>

                <div className="mt-10 flex items-center justify-between gap-4">
                  {step > 0 ? (
                    <button
                      type="button"
                      onClick={goBack}
                      data-testid="quote-back-button"
                      className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ivory/50 hover:text-amber transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={loading}
                    data-testid={isLast ? "quote-submit-button" : "quote-next-button"}
                    className="group inline-flex items-center gap-3 rounded-full bg-amber px-8 py-4 text-base font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" /> Envoi…
                      </>
                    ) : isLast ? (
                      <>
                        Recevoir mon devis gratuit
                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    ) : (
                      <>
                        Continuer
                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>

                {isLast && (
                  <p className="mt-6 flex items-center justify-center gap-2 text-xs text-ivory/40" data-testid="quote-rgpd-note">
                    <Lock className="h-3.5 w-3.5 text-amber/70" />
                    Vos données sont confidentielles et protégées selon la norme RGPD.
                  </p>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-ivory/40 text-sm">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber" /> Sans engagement</span>
              <span className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-amber" /> Réponse sous 24h</span>
              <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-amber" /> Conseiller dédié</span>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
